import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { pillarImages } from "@/mocks/images";
import { useLocalizedLinks } from "@/hooks/useLocale";

interface PillarCard {
  slug: string;
  title: string;
  description: string;
  category: string;
  updated: string;
}

export default function ArticleSection() {
  const { t } = useTranslation();
  const { to: linkTo } = useLocalizedLinks();
  const articles = t("pillars.articles", { returnObjects: true }) as PillarCard[];

  return (
    <section id="guides" className="bg-background-100 py-14 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            {t("pillars.sectionTitle")}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-foreground-700 md:text-base">
            {t("pillars.sectionSubtitle")}
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {articles.map((a) => (
            <Link
              key={a.slug}
              to={linkTo(`/guides/${a.slug}`)}
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
            >
              <div className="h-44 w-full overflow-hidden">
                <img
                  src={pillarImages[a.slug] ?? pillarImages["usfans-review"]}
                  alt={a.title}
                  title={a.title}
                  className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-5">
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
                    {a.category}
                  </span>
                  <span className="whitespace-nowrap text-xs text-foreground-600">
                    {t("pillars.updatedLabel")} {a.updated}
                  </span>
                </div>
                <h3 className="mt-3 font-heading text-base font-semibold leading-snug text-foreground-950 md:text-lg">
                  {a.title}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-700">{a.description}</p>
                <span className="mt-4 inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-primary-700 transition-colors group-hover:text-primary-600">
                  {t("pillars.readMore")}
                  <span className="flex h-4 w-4 items-center justify-center transition-transform group-hover:translate-x-1">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </span>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}